import Skeleton from 'react-loading-skeleton';

import { FixtureRow } from './FixtureRow';
import { GameweekHeader } from './GameweekHeader';
import { TeamCell } from './TeamCell';
import { MAX_GAMEWEEK, MIN_GAMEWEEK } from './constants';
import { groupFixturesByGameweek } from './utils';
import { useTeamFixturesPreview } from '@hooks/useTeamFixturesPreview';
import { useLeagueContext } from '@hooks/useLeagueContext';

const MINI_GAMEWEEK_HORIZON = 3;
const MINI_TEAM_COLUMN_WIDTH = 84;
const MINI_FIXTURE_CELL_WIDTH = 60;
const MINI_ROW_HEIGHT = 28;
const MINI_HEADER_HEIGHT = 22;
const SKELETON_ROW_COUNT = 20;

export const MiniFixtureTicker = () => {
  const { context: leagueContext, isLoading: contextIsLoading } =
    useLeagueContext();
  // After the final gameweek the context still reports the finished season with
  // a null nextGameweek, but the schedule worth surfacing belongs to the new
  // season, which is announced before it kicks off. The backend resolves its
  // default season from the current gameweek, of which there is none in the
  // off-season, so it returns nothing unless we name the season.
  const contextSeason = leagueContext?.season;
  const isPostSeason = leagueContext?.seasonStatus === 'POST_SEASON';
  const targetSeason =
    contextSeason === undefined
      ? undefined
      : isPostSeason
      ? contextSeason + 1
      : contextSeason;
  const nextGameweek = leagueContext?.nextGameweek?.number ?? MIN_GAMEWEEK;
  const gameweekStart = nextGameweek;
  const gameweekEnd = Math.min(
    nextGameweek + MINI_GAMEWEEK_HORIZON - 1,
    MAX_GAMEWEEK
  );
  const visibleGameweeks = Array.from(
    { length: gameweekEnd - gameweekStart + 1 },
    (_, index) => gameweekStart + index
  );

  const { teams, isLoading: fixturesIsLoading } = useTeamFixturesPreview({
    gameweekStart,
    gameweekEnd,
    season: targetSeason
  });

  if (contextIsLoading || fixturesIsLoading) {
    return (
      <div className="p-2">
        <Skeleton count={SKELETON_ROW_COUNT} />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col text-xs">
      <div className="flex items-center border-b border-neutral-200 py-1">
        <div style={{ width: MINI_TEAM_COLUMN_WIDTH }} />
        <GameweekHeader
          gameweeks={visibleGameweeks}
          fixtureCellWidth={MINI_FIXTURE_CELL_WIDTH}
          rowHeight={MINI_HEADER_HEIGHT}
          stretch
        />
      </div>
      {teams.map((team) => {
        const gameweeks = groupFixturesByGameweek(
          team.fixtures,
          gameweekStart,
          gameweekEnd
        );
        return (
          <div
            key={team.shortName}
            data-testid={`mini-fixture-ticker-row-${team.shortName}`}
            className="flex items-center border-b border-neutral-100"
          >
            <div style={{ width: MINI_TEAM_COLUMN_WIDTH }}>
              <TeamCell shortName={team.shortName} height={MINI_ROW_HEIGHT} />
            </div>
            <FixtureRow
              gameweeks={gameweeks}
              fixtureCellWidth={MINI_FIXTURE_CELL_WIDTH}
              rowHeight={MINI_ROW_HEIGHT}
              stretch
            />
          </div>
        );
      })}
    </div>
  );
};
